/**
 * Wishlist / Favorites Module — global layer
 *
 * Owns the binary heart state, the header count, the login prompt and the
 * shared same-origin JSON request layer. The /pages/wishlist rendering lives in
 * wishlist-page.js and wishlist-groups.js, which build on the helpers exposed
 * through window.Wishlist.
 *
 * Relies on window.__wishlist for customer state (injected via Liquid).
 */
(function () {
  'use strict';

  /* -----------------------------------------------------------------------
     Configuration
     ----------------------------------------------------------------------- */

  var API = {
    list:       '/apps/wishlist',
    add:        '/apps/wishlist/add',
    remove:     '/apps/wishlist/remove',
    listCreate: '/apps/wishlist/lists/create',
    listRename: '/apps/wishlist/lists/rename',
    listDelete: '/apps/wishlist/lists/delete',
  };

  var SELECTORS = {
    toggle:      '[data-wishlist-toggle]',
    headerCount: '[data-wishlist-header-count]',
    loginPrompt: '#wishlist-login-prompt',
  };

  var ANIMATION_DURATION = 350;
  var LOGIN_PROMPT_DURATION = 3500;
  var CACHE_KEY = 'wishlist_ids';
  var CACHE_TTL = 60000; // 60 seconds

  /** Total attempts (initial + retries) for a 429 response */
  var MAX_ATTEMPTS = 3;
  var BASE_RETRY_DELAY = 500;

  /** Statuses the UI handles on purpose — never logged */
  var EXPECTED_STATUSES = { 401: true, 404: true, 422: true, 429: true };

  /* -----------------------------------------------------------------------
     State
     ----------------------------------------------------------------------- */

  /** @type {Set<string>} Product IDs currently in the wishlist (as strings) */
  var wishlistIds = new Set();

  /** Whether the initial fetch has completed */
  var initialized = false;

  /** Whether a fetch is in-flight */
  var fetching = false;

  /** Tracks in-flight API calls to prevent double-clicks */
  var pendingRequests = new Set();

  /** Debounce flag for MutationObserver */
  var updateScheduled = false;

  /* -----------------------------------------------------------------------
     Helpers
     ----------------------------------------------------------------------- */

  function hasOwn(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
  }

  function getConfig() {
    return window.__wishlist || { isLoggedIn: false, customerId: null, loginUrl: '/account/login' };
  }

  function isLoggedIn() {
    var cfg = getConfig();
    return !!(cfg.isLoggedIn && cfg.customerId);
  }

  /**
   * Normalize product ID to a simple numeric string.
   * Accepts: 12345, "12345", "gid://shopify/Product/12345"
   */
  function normalizeId(id) {
    if (!id && id !== 0) return '';
    var str = String(id);
    var gidMatch = str.match(/Product\/(\d+)/);
    return gidMatch ? gidMatch[1] : str.replace(/\D/g, '');
  }

  /** String-based HTML escaping — no DOM allocation */
  function escapeHtml(str) {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* -----------------------------------------------------------------------
     Shared request layer
     ----------------------------------------------------------------------- */

  function firstFieldMessage(bag) {
    var key;
    for (key in bag) {
      if (hasOwn(bag, key)) {
        var val = bag[key];
        if (Array.isArray(val) && val.length) return String(val[0]);
        if (typeof val === 'string' && val) return val;
      }
    }
    return '';
  }

  /**
   * Turn a non-OK response body into an Error carrying the status and any
   * field-level error bag. Supports `errors` (object bag or array),
   * `error` (string) and `message` (string).
   */
  function buildError(status, body) {
    body = body || {};

    var fieldErrors = null;
    var message = '';

    if (body.errors) {
      if (Array.isArray(body.errors)) {
        message = body.errors.join(' ');
      } else if (typeof body.errors === 'object') {
        fieldErrors = body.errors;
      } else if (typeof body.errors === 'string') {
        message = body.errors;
      }
    }
    if (!message && typeof body.error === 'string') message = body.error;
    if (!message && typeof body.message === 'string') message = body.message;
    if (!message && fieldErrors) message = firstFieldMessage(fieldErrors);

    var err = new Error(message || 'Request failed');
    err.status = status;
    err.fieldErrors = fieldErrors;
    err.body = body;
    err.expected = !!EXPECTED_STATUSES[status];
    return err;
  }

  function isExpectedError(err) {
    return !!(err && err.status && EXPECTED_STATUSES[err.status]);
  }

  /** Single console.warn for terminal, unexpected network / 5xx failures only */
  function warnUnexpected(context, err) {
    if (isExpectedError(err)) return;
    console.warn('[Wishlist] ' + context + ':', err && err.message ? err.message : err);
  }

  function retryDelay(headerValue, attempt) {
    var fallback = BASE_RETRY_DELAY * Math.pow(2, attempt - 1);
    if (!headerValue) return fallback;
    var raw = String(headerValue).trim();
    if (/^\d+$/.test(raw)) return Math.max(0, parseInt(raw, 10) * 1000);
    var when = Date.parse(raw);
    if (!isNaN(when)) return Math.max(0, when - Date.now());
    return fallback;
  }

  function delay(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  function readBody(res) {
    if (res.status === 204) return Promise.resolve({});
    return res.json().then(
      function (body) { return body && typeof body === 'object' ? body : {}; },
      function () { return {}; }
    );
  }

  /**
   * Same-origin JSON request. Never appends auth parameters — the app proxy
   * authenticates through the storefront session cookie.
   *
   * options: { method, data (auto JSON.stringify), body, attempt }
   */
  function request(url, options) {
    options = options || {};
    var attempt = options.attempt || 1;
    var body = options.body;
    if (body === undefined && options.data !== undefined) {
      body = JSON.stringify(options.data);
    }

    var init = {
      method: options.method || 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
    };
    if (body !== undefined) init.body = body;

    return fetch(url, init).then(function (res) {
      return readBody(res).then(function (parsed) {
        if (res.ok) return parsed;

        if (res.status === 429 && attempt < MAX_ATTEMPTS) {
          var header = res.headers && res.headers.get ? res.headers.get('Retry-After') : null;
          return delay(retryDelay(header, attempt)).then(function () {
            return request(url, {
              method: options.method,
              body: body,
              attempt: attempt + 1,
            });
          });
        }

        throw buildError(res.status, parsed);
      });
    });
  }

  /* -----------------------------------------------------------------------
     SessionStorage Cache
     ----------------------------------------------------------------------- */

  function getCachedIds() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (Date.now() - data.ts > CACHE_TTL) {
        sessionStorage.removeItem(CACHE_KEY);
        return null;
      }
      return data.ids;
    } catch (e) {
      return null;
    }
  }

  function setCachedIds() {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({
        ids: Array.from(wishlistIds),
        ts: Date.now(),
      }));
    } catch (e) {
      // Storage full or unavailable — silently ignore
    }
  }

  function clearCache() {
    try { sessionStorage.removeItem(CACHE_KEY); } catch (e) {}
  }

  /* -----------------------------------------------------------------------
     API
     ----------------------------------------------------------------------- */

  /**
   * Replace the tracked product IDs wholesale. Used by the wishlist page after
   * a group mutation, where the authoritative set is derived from all entries.
   */
  function setIds(ids) {
    var next = new Set();
    if (ids && ids.length) {
      for (var i = 0; i < ids.length; i++) {
        var nid = normalizeId(ids[i]);
        if (nid) next.add(nid);
      }
    }
    wishlistIds = next;
    initialized = true;
    setCachedIds();
    updateAllUI();
    publishWishlistChange();
  }

  function fetchWishlist() {
    if (!isLoggedIn() || fetching) return Promise.resolve();

    // Serve from cache when available (avoids app-proxy round-trip)
    var cached = getCachedIds();
    if (cached) {
      wishlistIds = new Set(cached);
      initialized = true;
      updateAllUI();
      return Promise.resolve();
    }

    fetching = true;

    return request(API.list)
      .then(function (data) {
        wishlistIds.clear();
        if (data && Array.isArray(data.items)) {
          data.items.forEach(function (item) {
            var ref = item.product && item.product.reference
              ? item.product.reference
              : null;
            if (ref && ref.id) {
              wishlistIds.add(normalizeId(ref.id));
            }
          });
        }
        initialized = true;
        setCachedIds();
        updateAllUI();
      })
      .catch(function (err) {
        warnUnexpected('Fetch failed', err);
      })
      .finally(function () {
        fetching = false;
      });
  }

  function addToWishlist(productId, triggerBtn) {
    var nid = normalizeId(productId);
    if (!nid || pendingRequests.has(nid)) return Promise.resolve();

    pendingRequests.add(nid);

    // Optimistic UI
    wishlistIds.add(nid);
    updateAllUI();
    if (triggerBtn) {
      animateHeart(triggerBtn);
    }

    return request(API.add, {
      method: 'POST',
      data: { product_id: nid },
    })
      .then(function () {
        setCachedIds();
        publishWishlistChange();
      })
      .catch(function (err) {
        // Revert on failure
        wishlistIds.delete(nid);
        updateAllUI();
        warnUnexpected('Add failed', err);
      })
      .finally(function () {
        pendingRequests.delete(nid);
      });
  }

  function removeFromWishlist(productId) {
    var nid = normalizeId(productId);
    if (!nid || pendingRequests.has(nid)) return Promise.resolve();

    pendingRequests.add(nid);

    // Optimistic UI
    wishlistIds.delete(nid);
    updateAllUI();

    return request(API.remove, {
      method: 'POST',
      data: { product_id: nid },
    })
      .then(function () {
        setCachedIds();
        publishWishlistChange();
      })
      .catch(function (err) {
        // Revert on failure
        wishlistIds.add(nid);
        updateAllUI();
        warnUnexpected('Remove failed', err);
      })
      .finally(function () {
        pendingRequests.delete(nid);
      });
  }

  /* -----------------------------------------------------------------------
     PubSub Integration
     ----------------------------------------------------------------------- */

  function publishWishlistChange() {
    if (typeof window.publish === 'function') {
      window.publish('wishlist:change', {
        ids: Array.from(wishlistIds),
        count: wishlistIds.size,
      });
    }
  }

  /* -----------------------------------------------------------------------
     UI Updates
     ----------------------------------------------------------------------- */

  function updateAllUI() {
    updateHeartButtons();
    updateHeaderCount();
  }

  function updateHeartButtons() {
    var btns = document.querySelectorAll(SELECTORS.toggle);
    var strings = window.__wishlistStrings || {};
    for (var i = 0; i < btns.length; i++) {
      var btn = btns[i];
      var nid = normalizeId(btn.getAttribute('data-product-id'));
      var inWishlist = wishlistIds.has(nid);
      btn.setAttribute('aria-pressed', String(inWishlist));

      if (inWishlist) {
        btn.setAttribute('aria-label', strings.remove || 'Remove from wishlist');
        btn.setAttribute('title', strings.remove || 'Remove from wishlist');
      } else {
        btn.setAttribute('aria-label', strings.add || 'Add to wishlist');
        btn.setAttribute('title', strings.add || 'Add to wishlist');
      }

      var label = btn.querySelector('.wishlist-product-action__label');
      if (label) {
        label.textContent = inWishlist
          ? (strings.remove || 'Remove from wishlist')
          : (strings.add || 'Add to wishlist');
      }
    }
  }

  function updateHeaderCount() {
    var els = document.querySelectorAll(SELECTORS.headerCount);
    var count = wishlistIds.size;
    for (var i = 0; i < els.length; i++) {
      els[i].textContent = count > 0 ? count : '';
      els[i].setAttribute('data-count', count);
    }
  }

  /** Animate a single heart button (called with the clicked element directly) */
  function animateHeart(btn) {
    btn.classList.remove('wishlist-heart--animate');
    void btn.offsetWidth; // Force reflow for animation restart
    btn.classList.add('wishlist-heart--animate');
    setTimeout(function () {
      btn.classList.remove('wishlist-heart--animate');
    }, ANIMATION_DURATION);
  }

  function setLoading(btn, loading) {
    btn.classList.toggle('wishlist-heart--loading', loading);
    btn.disabled = loading;
  }

  /* -----------------------------------------------------------------------
     Login Prompt
     ----------------------------------------------------------------------- */

  var loginPromptTimer = null;

  function showLoginPrompt() {
    var prompt = document.querySelector(SELECTORS.loginPrompt);
    if (!prompt) return;

    clearTimeout(loginPromptTimer);
    prompt.classList.add('wishlist-login-prompt--visible');

    loginPromptTimer = setTimeout(function () {
      prompt.classList.remove('wishlist-login-prompt--visible');
    }, LOGIN_PROMPT_DURATION);
  }

  /* -----------------------------------------------------------------------
     Event Handling
     ----------------------------------------------------------------------- */

  function handleToggleClick(event) {
    var btn = event.target.closest(SELECTORS.toggle);
    if (!btn) return;

    event.preventDefault();
    event.stopPropagation();

    if (!isLoggedIn()) {
      showLoginPrompt();
      return;
    }

    var nid = normalizeId(btn.getAttribute('data-product-id'));
    if (!nid || pendingRequests.has(nid)) return;

    setLoading(btn, true);

    var promise;
    if (wishlistIds.has(nid)) {
      promise = removeFromWishlist(nid);
    } else {
      promise = addToWishlist(nid, btn);
    }

    promise.finally(function () {
      setLoading(btn, false);
    });
  }

  /* -----------------------------------------------------------------------
     Initialization
     ----------------------------------------------------------------------- */

  function init() {
    // Delegate click events for heart toggles
    document.addEventListener('click', handleToggleClick);

    // Fetch wishlist on load for logged-in customers
    if (isLoggedIn()) {
      fetchWishlist();
    }

    // Observe DOM for dynamically added hearts (e.g., quick-add modals, AJAX pagination)
    // Debounced via requestAnimationFrame to batch rapid DOM mutations
    var observer = new MutationObserver(function () {
      if (!initialized || updateScheduled) return;
      updateScheduled = true;
      requestAnimationFrame(function () {
        updateScheduled = false;
        updateHeartButtons();
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Public API (backward-compatible + shared helpers for the page modules)
  window.Wishlist = {
    API: API,
    add: addToWishlist,
    remove: removeFromWishlist,
    has: function (productId) { return wishlistIds.has(normalizeId(productId)); },
    getIds: function () { return Array.from(wishlistIds); },
    getCount: function () { return wishlistIds.size; },
    refresh: function () { clearCache(); return fetchWishlist(); },
    setIds: setIds,
    request: request,
    normalizeId: normalizeId,
    escapeHtml: escapeHtml,
    isLoggedIn: isLoggedIn,
    getConfig: getConfig,
    isExpectedError: isExpectedError,
    warnUnexpected: warnUnexpected,
    clearCache: clearCache,
    updateUI: updateAllUI,
  };

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
